import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Clock, Facebook, Twitter, Instagram, Youtube, MessageSquare } from 'lucide-react';

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-asphalt text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center mb-6">
              <img src="/logo-light.png" alt="iBookSport" className="h-10" />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-slate-400 hover:text-brand transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-slate-400 hover:text-brand transition-colors" aria-label="Twitter">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="text-slate-400 hover:text-brand transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="text-slate-400 hover:text-brand transition-colors" aria-label="Youtube">
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Sports */}
          <div>
            <h3 className="font-heading font-bold text-lg uppercase tracking-wider mb-6">Sports</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/events?sport_type=cycling" className="text-slate-400 hover:text-white transition-colors">
                  {t('map.cycling')}
                </Link>
              </li>
              <li>
                <Link to="/events?sport_type=running" className="text-slate-400 hover:text-white transition-colors">
                  {t('map.running')}
                </Link>
              </li>
              <li>
                <Link to="/events?sport_type=triathlon" className="text-slate-400 hover:text-white transition-colors">
                  {t('map.triathlon')}
                </Link>
              </li>
              <li>
                <Link to="/events?sport_type=crossfit" className="text-slate-400 hover:text-white transition-colors">
                  {t('map.crossfit')}
                </Link>
              </li>
              <li>
                <Link to="/events?sport_type=vtt" className="text-slate-400 hover:text-white transition-colors">
                  {t('map.vtt')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Links */}
          <div>
            <h3 className="font-heading font-bold text-lg uppercase tracking-wider mb-6">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/events" className="text-slate-400 hover:text-white transition-colors">
                  {t('nav.events')}
                </Link>
              </li>
              <li>
                <Link to="/register" className="text-slate-400 hover:text-white transition-colors">
                  {t('nav.register')}
                </Link>
              </li>
              <li>
                <Link to="/organizers" className="text-slate-400 hover:text-white transition-colors">
                  {t('nav.organizers')}
                </Link>
              </li>
              <li>
                <Link to="/cgv" className="text-slate-400 hover:text-white transition-colors">
                  {t('footer.cgv')}
                </Link>
              </li>
              <li>
                <Link to="/confidentialite" className="text-slate-400 hover:text-white transition-colors">
                  {t('footer.privacy')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading font-bold text-lg uppercase tracking-wider mb-6">{t('footer.contact')}</h3>
            <ul className="space-y-4">
              <li className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-brand flex-shrink-0" />
                <span className="text-slate-400 text-sm">{t('footer.available')}</span>
              </li>
              <li className="pt-1">
                <Link to="/contact" className="inline-flex items-center gap-2 bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase tracking-wider text-xs px-5 py-2.5 transition-colors" data-testid="footer-contact-btn">
                  <MessageSquare className="w-4 h-4" /> Contactez-nous
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-slate-500 text-sm">
              &copy; 2026 iBookSport. {t('footer.rights')}. Conception Plateforme{' '}
              <a href="https://www.webisula.com/" target="_blank" rel="noopener noreferrer" className="text-brand font-bold hover:underline">{t('footer.company')}</a>.
            </p>
            <div className="flex items-center space-x-4 text-sm">
              <span className="text-slate-500">SQUARE</span>
              <div className="flex items-center gap-2">
                <svg viewBox="0 0 48 48" className="h-7 w-10 rounded bg-white/10 p-1" aria-label="CB"><rect width="48" height="48" rx="4" fill="#1A1F71"/><text x="24" y="30" textAnchor="middle" fill="#fff" fontSize="16" fontWeight="bold" fontFamily="Arial">CB</text></svg>
                <svg viewBox="0 0 48 48" className="h-7 w-10 rounded bg-white/10 p-1" aria-label="Visa"><rect width="48" height="48" rx="4" fill="#1A1F71"/><text x="24" y="32" textAnchor="middle" fill="#F7B600" fontSize="14" fontWeight="bold" fontStyle="italic" fontFamily="Arial">VISA</text></svg>
                <svg viewBox="0 0 48 48" className="h-7 w-10 rounded bg-white/10 p-1" aria-label="Mastercard"><rect width="48" height="48" rx="4" fill="#000"/><circle cx="19" cy="24" r="11" fill="#EB001B"/><circle cx="29" cy="24" r="11" fill="#F79E1B"/><path d="M24 15.7a11 11 0 010 16.6 11 11 0 000-16.6z" fill="#FF5F00"/></svg>
                <svg viewBox="0 0 48 48" className="h-7 w-10 rounded bg-white/10 p-1" aria-label="Amex"><rect width="48" height="48" rx="4" fill="#2E77BC"/><text x="24" y="30" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold" fontFamily="Arial">AMEX</text></svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
